import type { IntegrationProvider } from '@brew/integration-contracts'
import { z } from 'zod'

const apiKeySchema = z
  .object({ apiKey: z.string().trim().min(1).max(4096) })
  .strict()

const oauthSchema = z.object({}).strict()

const webhookSecretSchema = z
  .object({ apiKey: z.string().trim().min(1).max(4096) })
  .strict()

export const PROVIDER_SETUP_SCHEMAS = {
  braze: z
    .object({
      apiKey: z.string().trim().min(1).max(4096),
      instance: z.string().trim().min(1).max(255),
      appGroupId: z.string().trim().min(1).max(255).optional(),
    })
    .strict(),
  brevo: apiKeySchema,
  clerk: z
    .object({ apiKey: z.string().trim().startsWith('whsec_').max(4096) })
    .strict(),
  customerio: z
    .object({
      apiKey: z.string().trim().min(1).max(4096),
      region: z.enum(['us', 'eu']).default('us'),
    })
    .strict(),
  framer: z.object({}).strict(),
  figma: oauthSchema,
  slack: oauthSchema,
  hubspot: oauthSchema,
  typeform: oauthSchema,
  klaviyo: oauthSchema,
  iterable: z
    .object({
      apiKey: z.string().trim().min(1).max(4096),
      region: z.enum(['us', 'eu']).default('us'),
    })
    .strict(),
  postmark: apiKeySchema,
  loops: apiKeySchema,
  onesignal: z
    .object({
      apiKey: z.string().trim().min(1).max(4096),
      appId: z.string().uuid(),
    })
    .strict(),
  mailgun: z
    .object({
      apiKey: z.string().trim().min(1).max(4096),
      domain: z.string().trim().min(1).max(255),
      region: z.enum(['us', 'eu']).default('us'),
    })
    .strict(),
  sendgrid: z
    .object({
      apiKey: z.string().trim().min(1).max(4096),
      region: z.enum(['us', 'eu']).default('us'),
    })
    .strict(),
  mailjet: z
    .object({
      apiKey: z.string().trim().min(1).max(4096),
      secretKey: z.string().trim().min(1).max(4096),
    })
    .strict(),
  mailchimp: oauthSchema,
  stripe: oauthSchema,
  stytch: z
    .object({
      apiKey: z.string().trim().startsWith('whsec_').max(4096),
      stytchProject: z.enum(['consumer', 'b2b']),
    })
    .strict(),
  supabase: webhookSecretSchema,
  workos: webhookSecretSchema,
  shopify: z
    .object({
      apiKey: z.string().trim().min(1).max(4096),
      domain: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/),
    })
    .strict(),
  revenuecat: webhookSecretSchema,
} satisfies Record<IntegrationProvider, z.ZodType<Record<string, unknown>>>

export const PROVIDER_SETUP_EXAMPLES = {
  braze: { apiKey: 'live-test-key', instance: 'US-01' },
  brevo: { apiKey: 'live-test-key' },
  clerk: { apiKey: 'whsec_live_test' },
  customerio: { apiKey: 'live-test-key', region: 'us' },
  framer: {},
  figma: {},
  slack: {},
  hubspot: {},
  typeform: {},
  klaviyo: {},
  iterable: { apiKey: 'live-test-key', region: 'us' },
  postmark: { apiKey: 'live-test-key' },
  loops: { apiKey: 'live-test-key' },
  onesignal: {
    apiKey: 'live-test-key',
    appId: '00000000-0000-4000-8000-000000000000',
  },
  mailgun: {
    apiKey: 'live-test-key',
    domain: 'mg.example.test',
    region: 'us',
  },
  sendgrid: { apiKey: 'live-test-key', region: 'us' },
  mailjet: { apiKey: 'live-test-key', secretKey: 'live-test-secret' },
  mailchimp: {},
  stripe: {},
  stytch: { apiKey: 'whsec_live_test', stytchProject: 'consumer' },
  supabase: { apiKey: 'live-test-secret' },
  workos: { apiKey: 'live-test-secret' },
  shopify: {
    apiKey: 'live-test-secret',
    domain: 'live-test.myshopify.com',
  },
  revenuecat: { apiKey: 'live-test-secret' },
} satisfies Record<IntegrationProvider, Record<string, unknown>>

/**
 * Projects first-party form state onto a provider's canonical setup shape.
 *
 * Integration forms share one UI state type, so they can contain fields owned
 * by another provider or client-only setup fields. The server remains strict;
 * this client-side projection ensures it receives only the selected provider's
 * contract fields.
 */
export function projectProviderSetupInput(
  provider: IntegrationProvider,
  input: unknown
) {
  return PROVIDER_SETUP_SCHEMAS[provider].strip().safeParse(input)
}
